import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { heartbeatApi } from '../utils/api';
import { getApiUrl } from '../utils/apiUrl';

interface HeartbeatContextType {
    isInitialized: boolean;
    lastHeartbeat: Date | null;
    connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
}

const HeartbeatContext = createContext<HeartbeatContextType>({ 
    isInitialized: false,
    lastHeartbeat: null,
    connectionStatus: 'disconnected'
});

export const useHeartbeat = () => useContext(HeartbeatContext);

// Global heartbeat coordination
const HEARTBEAT_LOCK_KEY = 'heartbeat_active_lock';
const HEARTBEAT_INTERVAL = 15000; // 15 seconds

// Auto-offline detection settings
const AUTO_OFFLINE_TIMEOUT = 30000; // 30 seconds - mark user offline if no heartbeat
const BROWSER_CLOSE_TIMEOUT = 5000; // 5 seconds - mark offline when browser closes

// Cleanup stale heartbeat locks
const cleanupStaleHeartbeatLocks = () => {
    const keys = Object.keys(localStorage);
    const now = Date.now();
    
    keys.forEach(key => {
        if (key.startsWith(HEARTBEAT_LOCK_KEY)) {
            try {
                const lockData = JSON.parse(localStorage.getItem(key) || '{}');
                const lockAge = now - (lockData.timestamp || 0);
                
                // Remove locks older than 2 minutes
                if (lockAge > 120000) {
                    localStorage.removeItem(key);
                    // Cleaned up stale heartbeat lock
                }
            } catch (error) {
                localStorage.removeItem(key);
                // Cleaned up corrupted heartbeat lock
            }
        }
    });
};

// Run cleanup every 30 seconds
setInterval(cleanupStaleHeartbeatLocks, 30000);

export const HeartbeatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, user, logout } = useAuth();
    const navigate = useNavigate();
    const [lastHeartbeat, setLastHeartbeat] = useState<Date | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('disconnected');
    const [failedAttempts, setFailedAttempts] = useState(0);
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
    
    // Auto-offline detection refs
    const autoOfflineTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastActivityRef = useRef<number>(Date.now());

    // Function to mark user as offline when they don't logout properly
    const markUserOffline = useCallback(async () => {
        try {
            if (!user || !isAuthenticated) return;
            
            // Skip offline marking for Super Admin Viewers since they're temporary users
            if (user.role === 'super_admin_viewer' || user.isSuperAdminViewer) {
                // Super Admin Viewer detected - skipping offline marking (temporary user)
                return;
            }
            
            // Send offline status to backend using the general offline endpoint
            const authToken = sessionStorage.getItem('temp_client_token') || sessionStorage.getItem('token');
            if (!authToken) return;
            
            // Use the general offline endpoint that handles all user types
            const endpoint = `${getApiUrl()}/api/auth/offline`;
            
            await heartbeatApi.post(endpoint, {}, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            
            // console.log('🔴 User marked as offline due to inactivity/disconnection');
        } catch (error) {
            console.warn('⚠️ Failed to mark user offline:', error);
        }
    }, [user, isAuthenticated]);

    const sendHeartbeat = useCallback(async () => {
        try {
            const token = sessionStorage.getItem('token');
            if (!token || !isAuthenticated || !user) {
                setLastHeartbeat(null);
                setConnectionStatus('disconnected');
                setIsInitialized(false);
                return;
            }

            // Security: Validate user and token integrity
            if (!user.role || typeof user.role !== 'string') {
                console.warn('⚠️ Invalid user role detected, skipping heartbeat');
                return;
            }

            let tokenRole = 'admin'; // Default value
            let isSuperAdminViewer = false;

            // Check if we're in temporary access mode
            const isTemporaryAccess = window.location.search.includes('temp_access=true') || 
                                    sessionStorage.getItem('temp_client_token');
            
            // Use temporary token if available, otherwise use regular token
            const authToken = sessionStorage.getItem('temp_client_token') || token;
            
            // Validate token matches user role to prevent cross-contamination
            try {
                const tokenPayload = JSON.parse(atob(authToken.split('.')[1]));
                tokenRole = tokenPayload.role || 'admin';
                const tokenCompanyName = tokenPayload.companyName;
                
                
                // Handle super admin viewer role - treat it as client for validation
                const effectiveTokenRole = tokenRole === 'super_admin_viewer' ? 'client' : tokenRole;
                const effectiveUserRole = user.role === 'super_admin_viewer' ? 'client' : user.role;
                
                // Set super admin viewer flag
                isSuperAdminViewer = tokenRole === 'super_admin_viewer' || user.role === 'super_admin_viewer';
                
                // Special handling for super admin viewer sessions
                if (tokenRole === 'super_admin_viewer' || user.role === 'super_admin_viewer') {
                    const sessionId = sessionStorage.getItem('super_admin_session_id');
                    // Super Admin Viewer session validation
                    
                    // Always allow super admin viewer sessions to continue
                    if (isTemporaryAccess) {
                        // Super Admin Viewer session validated successfully
                        // Continue with heartbeat
                    } else {
                        console.warn('⚠️ Super Admin Viewer session without temp_access flag - this should not happen');
                    }
                }
                
                // TEMPORARILY DISABLED for local development debugging
                // if (window.location.pathname.includes('/client/') && tokenRole === 'admin' && !tokenCompanyName && !isTemporaryAccess) {
                //     // CRITICAL: Check if logout is in progress to prevent redirect conflicts
                //     if (sessionStorage.getItem('logoutInProgress')) {
                //         console.log('🚨 Security: Admin token detected on client page - but logout in progress, skipping redirect');
                //         return;
                //     }
                    
                //     console.warn('🚨 Security: Admin token detected on client page - clearing and redirecting');
                //     sessionStorage.clear();
                //     localStorage.clear();
                    
                //     // Determine redirect based on company context
                //     const currentPath = window.location.pathname;
                //     const pathParts = currentPath.split('/');
                //     const companySlug = pathParts[1];
                    
                //     let redirectTo;
                //     if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug)) {
                //         redirectTo = `/${companySlug}/login`;
                //     } else {
                //         redirectTo = '/client-signup';
                //     }
                    
                //     window.location.href = redirectTo;
                //     return;
                // }
                
                // Skip token validation for temporary access
                // TEMPORARILY DISABLED for local development debugging
                // if (!isTemporaryAccess && (effectiveTokenRole !== effectiveUserRole || tokenPayload.id !== user.id)) {
                //     // CRITICAL: Check if logout is in progress to prevent redirect conflicts
                //     if (sessionStorage.getItem('logoutInProgress')) {
                //         console.log('🚨 Security: Token mismatch detected - but logout in progress, skipping redirect');
                //         return;
                //     }
                    
                //     console.warn(`🚨 Security: Token mismatch detected - token role: ${tokenRole}, user role: ${user.role}`);
                //     sessionStorage.removeItem('token');
                //     logout();
                //     return;
                // }
            } catch (tokenError) {
                // CRITICAL: Check if logout is in progress to prevent redirect conflicts
                if (sessionStorage.getItem('logoutInProgress')) {
                    return;
                }
                
                console.warn('🚨 Security: Invalid token format detected');
                sessionStorage.removeItem('token');
                logout();
                return;
            }

            // Choose the correct endpoint based on user type (admin vs client)
            // Super Admin Viewer should use client endpoints, not admin endpoints
            // BUT: Skip heartbeat entirely for Super Admin Viewers since they're temporary users
            if (isSuperAdminViewer) {
                // Super Admin Viewer detected - skipping heartbeat (temporary user)
                setLastHeartbeat(new Date());
                setConnectionStatus('connected');
                setIsInitialized(true);
                setFailedAttempts(0);
                return;
            }
            
            const heartbeatEndpoint = (window.location.pathname.includes('/admin/') && !isSuperAdminViewer)
                ? `${getApiUrl()}/api/auth/heartbeat`  // Real admin users only
                : `${getApiUrl()}/api/auth/client/heartbeat`;  // Client users (any role) + Super Admin Viewer

            const response = await heartbeatApi.post(
                heartbeatEndpoint,
                {},
                {
                    headers: { Authorization: `Bearer ${authToken}` }
                }
            );
            
            if (response.status === 200) {
                if (isAuthenticated && user) {
                    setLastHeartbeat(new Date());
                    setConnectionStatus('connected');
                    setIsInitialized(true);
                    setFailedAttempts(0);
                    
                    // Update last activity and reset auto-offline timer
                    lastActivityRef.current = Date.now();
                    
                    // Clear existing auto-offline timeout
                    if (autoOfflineTimeoutRef.current) {
                        clearTimeout(autoOfflineTimeoutRef.current);
                    }
                    
                    // Set new auto-offline timeout (skip for Super Admin Viewers)
                    if (!isSuperAdminViewer) {
                        autoOfflineTimeoutRef.current = setTimeout(() => {
                            // console.log('⏰ Auto-offline timeout reached - marking user offline');
                            markUserOffline();
                        }, AUTO_OFFLINE_TIMEOUT);
                    }
                }
            }
        } catch (error) {
            console.error('💓 Heartbeat error:', error);
            
            // Handle 403 errors - token conflict (admin token on client page)
            if (axios.isAxiosError(error) && error.response?.status === 403) {
                // CRITICAL: Check if logout is in progress to prevent redirect conflicts
                if (sessionStorage.getItem('logoutInProgress')) {
                    return;
                }
                
                console.warn('🚨 403 Forbidden in heartbeat - likely token conflict');
                console.warn('🧹 Clearing storage and redirecting to login');
                sessionStorage.clear();
                localStorage.clear();
                
                // Determine redirect based on company context
                const currentPath = window.location.pathname;
                const pathParts = currentPath.split('/');
                const companySlug = pathParts[1];
                
                let redirectTo;
                // Check if we have a saved login URL for this company
                const savedLoginUrl = sessionStorage.getItem('lastLoginUrl');
                if (savedLoginUrl && companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password'].includes(companySlug)) {
                    // Extract company slug from saved URL and redirect directly to company login
                    const savedPathParts = savedLoginUrl.split('/');
                    const savedCompanySlug = savedPathParts[1];
                    if (savedCompanySlug === companySlug) {
                        redirectTo = `/${companySlug}/login`;
                    } else {
                        redirectTo = `/${companySlug}/login`;
                    }
                } else if (companySlug && !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(companySlug)) {
                    redirectTo = `/${companySlug}/login`;
                } else {
                    redirectTo = '/client-signup';
                }
                
                window.location.href = redirectTo;
                return;
            }
            
            // Handle 401 errors - session expired or invalid
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                const errorData = error.response.data;
                
                if (user?.role === 'client' || user?.role === 'super_admin_viewer' || user?.isSuperAdminViewer) {
                    if (errorData.accountDeactivated) {
                        logout();
                        navigate('/account-deactivated');
                        return;
                                    } else {
                    // Don't redirect here - let the logout function handle the redirect
                    logout();
                    return;
                }
                } else if (user?.role === 'admin') {
                    logout();
                    // Let the logout function handle the redirect with company context
                    return;
                } else {
                    logout();
                    // Let the logout function handle the redirect with company context
                    return;
                }
            }
            
            const newFailedAttempts = failedAttempts + 1;
            setFailedAttempts(newFailedAttempts);
            
            if (newFailedAttempts >= 2) {
                setConnectionStatus('reconnecting');
                
                // Only log error message if it's not an auth issue
                if (axios.isAxiosError(error) && error.response?.status !== 401) {
                    console.warn('Connection lost. Attempting to reconnect...');
                }
            }
            
            setConnectionStatus('disconnected');
            setIsInitialized(false);
        }
    }, [isAuthenticated, user, failedAttempts, logout, navigate]);

    // Heartbeat management with single-session approach
    useEffect(() => {
        if (!isAuthenticated || !user) {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
            setConnectionStatus('disconnected');
            setLastHeartbeat(null);
            setIsInitialized(false);
            return;
        }

        const userId = user.id || user.username;
        const userRole = user.role || 'admin';
        const sessionId = sessionStorage.getItem('sessionId') || 'unknown';
        const heartbeatLock = `${HEARTBEAT_LOCK_KEY}_${userRole}_${userId}`;
        
        // Simple lock mechanism for single browser context
        const lockData = { sessionId, timestamp: Date.now() };
        
        try {
            localStorage.setItem(heartbeatLock, JSON.stringify(lockData));
            
            // Send initial heartbeat
            setTimeout(() => {
                if (isAuthenticated && user && sessionStorage.getItem('token')) {
                    sendHeartbeat();
                }
            }, 500);

            // Set up regular heartbeat interval
            intervalIdRef.current = setInterval(() => {
                // Verify we still have a valid session
                const currentSessionId = sessionStorage.getItem('sessionId');
                if (currentSessionId === sessionId && isAuthenticated && user) {
                    // Refresh lock and send heartbeat
                    const refreshedLockData = { sessionId: currentSessionId, timestamp: Date.now() };
                    localStorage.setItem(heartbeatLock, JSON.stringify(refreshedLockData));
                    sendHeartbeat();
                } else {

                    if (intervalIdRef.current) {
                        clearInterval(intervalIdRef.current);
                        intervalIdRef.current = null;
                    }
                }
            }, HEARTBEAT_INTERVAL);

        } catch (error) {
            console.warn('💓 Error setting up heartbeat:', error);
        }

        // Cleanup on unmount
        return () => {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
                intervalIdRef.current = null;
            }
            
            // Clear auto-offline timeout
            if (autoOfflineTimeoutRef.current) {
                clearTimeout(autoOfflineTimeoutRef.current);
                autoOfflineTimeoutRef.current = null;
            }
            
            try {
                const currentLockData = localStorage.getItem(heartbeatLock);
                if (currentLockData) {
                    const parsed = JSON.parse(currentLockData);
                    if (parsed.sessionId === sessionId) {
                        localStorage.removeItem(heartbeatLock);

                    }
                }
            } catch (error) {
                console.warn('💓 Error clearing heartbeat lock:', error);
        }
        };
    }, [isAuthenticated, user, sendHeartbeat]);

    // Handle window focus/blur for immediate sync
    useEffect(() => {
        let lastFocusHeartbeat = 0;
        
        const handleVisibilityChange = () => {
            if (isAuthenticated && user && document.visibilityState === 'visible') {
                const now = Date.now();
                if (now - lastFocusHeartbeat > 10000) { // 10 seconds rate limit
                    lastFocusHeartbeat = now;

                    sendHeartbeat();
                }
            }
        };

        const handleBeforeUnload = () => {
            if (isAuthenticated && user) {
                const token = sessionStorage.getItem('token');
                if (token) {
                    const userId = user.id || user.username;
                    const userRole = user.role || 'admin';
                    
                    // Send offline signal - use JSON instead of FormData for better compatibility
                    const offlineEndpoint = `${getApiUrl()}/api/auth/mark-offline`;
                    const data = JSON.stringify({ userId, userRole });
                    
                    try {
                        // Try JSON first, fallback to FormData if needed
                        const blob = new Blob([data], { type: 'application/json' });
                        navigator.sendBeacon(offlineEndpoint, blob);
                        // console.log('🔴 Offline signal sent successfully');
                    } catch (error) {
                        console.warn('🔴 Could not send offline signal:', error);
                        
                        // Fallback to FormData if JSON fails
                        try {
                            const formData = new FormData();
                            formData.append('userId', userId);
                            formData.append('userRole', userRole);
                            navigator.sendBeacon(offlineEndpoint, formData);
                            // console.log('🔴 Offline signal sent via FormData fallback');
                        } catch (fallbackError) {
                            console.warn('🔴 FormData fallback also failed:', fallbackError);
                        }
                    }
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        
        // Enhanced auto-offline detection for all user types
        const handlePageHide = () => {
            // Mark user as offline when page is hidden (tab switch, minimize, etc.)
            // Skip for Super Admin Viewers
            if (user?.role === 'super_admin_viewer' || user?.isSuperAdminViewer) {
                return;
            }
            setTimeout(() => {
                if (document.visibilityState === 'hidden') {
                    markUserOffline();
                }
            }, BROWSER_CLOSE_TIMEOUT);
        };

        const handleNetworkOffline = () => {
            console.log('🌐 Network connection lost - marking user offline');
            setConnectionStatus('disconnected');
            // Skip for Super Admin Viewers
            if (!(user?.role === 'super_admin_viewer' || user?.isSuperAdminViewer)) {
                markUserOffline();
            }
        };

        const handleNetworkOnline = () => {
            console.log('🌐 Network connection restored');
            setConnectionStatus('connected');
        };

        // User activity detection to reset auto-offline timer
        const handleUserActivity = () => {
            if (isAuthenticated && user) {
                lastActivityRef.current = Date.now();
                
                // Reset auto-offline timer on user activity
                if (autoOfflineTimeoutRef.current) {
                    clearTimeout(autoOfflineTimeoutRef.current);
                }
                
                // Skip auto-offline timeout for Super Admin Viewers
                if (!(user?.role === 'super_admin_viewer' || user?.isSuperAdminViewer)) {
                    autoOfflineTimeoutRef.current = setTimeout(() => {
                        // console.log('⏰ Auto-offline timeout reached after user activity - marking user offline');
                        markUserOffline();
                    }, AUTO_OFFLINE_TIMEOUT);
                }
            }
        };

        // Add event listeners for user activity
        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        activityEvents.forEach(event => {
            document.addEventListener(event, handleUserActivity, { passive: true });
        });

        // Add enhanced event listeners
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('offline', handleNetworkOffline);
        window.addEventListener('online', handleNetworkOnline);
        
        return () => {
            // Remove activity event listeners
            activityEvents.forEach(event => {
                document.removeEventListener(event, handleUserActivity);
            });
            
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('offline', handleNetworkOffline);
            window.removeEventListener('online', handleNetworkOnline);
            
            // Clear auto-offline timeout
            if (autoOfflineTimeoutRef.current) {
                clearTimeout(autoOfflineTimeoutRef.current);
            }
        };
    }, [isAuthenticated, user, sendHeartbeat, markUserOffline]);

    return (
        <HeartbeatContext.Provider value={{ 
            isInitialized, 
            lastHeartbeat,
            connectionStatus
        }}>
            {children}
        </HeartbeatContext.Provider>
    );
};